/**
 * @module  public/js/pages/forgot-password.js
 * @summary Handles password reset requests using either branded SMTP delivery
 *          or a Firebase Auth fallback, depending on backend configuration.
 */

import { auth, getAppCheckToken } from '/js/core/firebase-service.js';
import { sendPasswordResetEmail } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-auth.js';

export const FORGOT_PASSWORD_SUCCESS_MESSAGE =
  'If that email exists, a reset link has been sent.';
export const FORGOT_PASSWORD_FAILURE_MESSAGE =
  'Unable to start password reset right now. Please try again.';

/**
 * Decide whether a Firebase reset error is safe to collapse into the neutral
 * success message shown on the forgot-password page.
 *
 * @param {{code?:string}|null|undefined} error - Firebase Auth error object.
 * @returns {boolean} True when the page should still show success.
 */
export function isNeutralFirebaseResetError(error) {
  return String(error?.code || '').trim() === 'auth/user-not-found';
}

/**
 * Safely parse a JSON response body, returning an empty object on failure.
 *
 * @param {Response} response - Fetch response object.
 * @returns {Promise<object>} Parsed JSON payload.
 */
export async function parseJsonResponse(response) {
  try {
    return (await response.json()) || {};
  } catch {
    return {};
  }
}

/**
 * Create the forgot-password page controller.
 *
 * @param {object} [deps] - Dependency overrides for tests.
 * @param {Document} [deps.documentRef=document] - DOM document reference.
 * @param {(input:RequestInfo|URL, init?:RequestInit)=>Promise<Response>} [deps.fetchImpl=window.fetch.bind(window)]
 *        Fetch implementation used for the backend reset request.
 * @param {typeof auth} [deps.authInstance=auth] - Firebase Auth singleton.
 * @param {(auth:any, email:string)=>Promise<unknown>} [deps.sendPasswordResetEmailImpl=sendPasswordResetEmail]
 *        Firebase Auth password reset helper.
 * @param {(forceRefresh?:boolean)=>Promise<string|null>} [deps.getAppCheckTokenImpl=getAppCheckToken]
 *        App Check token resolver.
 * @param {Console} [deps.logger=console] - Logger implementation.
 * @returns {{
 *   init: () => void,
 *   handleSubmit: (event?: Event|{preventDefault?:Function}) => Promise<void>,
 *   requestPasswordResetEmail: (email:string) => Promise<object>
 * }} Page controller contract.
 */
export function createForgotPasswordPageController({
  documentRef = document,
  fetchImpl = null,
  authInstance = auth,
  sendPasswordResetEmailImpl = sendPasswordResetEmail,
  getAppCheckTokenImpl = getAppCheckToken,
  logger = console,
} = {}) {
  const form = documentRef.getElementById('resetForm');
  const responseEl = documentRef.getElementById('response');
  const emailInput = documentRef.getElementById('email');
  const submitButton =
    documentRef.getElementById('resetButton') ||
    form?.querySelector?.('button[type="submit"]');
  const resolvedFetchImpl =
    typeof fetchImpl === 'function'
      ? fetchImpl
      : typeof window !== 'undefined' && typeof window.fetch === 'function'
        ? window.fetch.bind(window)
        : null;

  /**
   * Render feedback copy inline on the page.
   *
   * @param {string} message - Text to display.
   * @param {'success'|'error'} tone - Visual tone for the message.
   * @returns {void}
   */
  function setResponse(message, tone) {
    if (!responseEl) return;
    responseEl.textContent = message;
    responseEl.style.color = tone === 'success' ? 'green' : 'red';
  }

  /**
   * Call the new backend reset endpoint and return its control payload.
   *
   * @param {string} email - Normalized email address.
   * @returns {Promise<object>} Backend control payload.
   */
  async function requestPasswordResetEmail(email) {
    const headers = new Headers({ 'Content-Type': 'application/json' });

    if (typeof getAppCheckTokenImpl === 'function') {
      try {
        const token = await getAppCheckTokenImpl(false);
        if (token) {
          headers.set('X-Firebase-AppCheck', token);
        }
      } catch (error) {
        logger.warn?.(
          '[forgot-password] Failed to resolve App Check token',
          error?.message || error
        );
      }
    }

    if (typeof resolvedFetchImpl !== 'function') {
      throw new Error('Fetch API is not available');
    }

    const response = await resolvedFetchImpl('/requestPasswordResetEmail', {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: JSON.stringify({ email }),
    });

    const payload = await parseJsonResponse(response);
    if (!response.ok) {
      const message =
        typeof payload?.error === 'string' && payload.error.trim()
          ? payload.error.trim()
          : FORGOT_PASSWORD_FAILURE_MESSAGE;
      throw new Error(message);
    }

    return payload;
  }

  /**
   * Submit the forgot-password flow.
   *
   * @param {Event|{preventDefault?:Function}} [event] - Submit event.
   * @returns {Promise<void>} Resolves after the page updates its UI state.
   */
  async function handleSubmit(event) {
    event?.preventDefault?.();

    const email = String(emailInput?.value || '').trim();
    if (!email) {
      setResponse('Enter a valid email address.', 'error');
      return;
    }

    if (submitButton) submitButton.disabled = true;
    setResponse('', 'success');

    try {
      const payload = await requestPasswordResetEmail(email);
      if (payload?.mode === 'firebase') {
        try {
          await sendPasswordResetEmailImpl(authInstance, email);
        } catch (error) {
          if (!isNeutralFirebaseResetError(error)) {
            throw error;
          }
        }
      } else if (payload?.mode !== 'smtp') {
        throw new Error('Unsupported password reset mode');
      }

      setResponse(FORGOT_PASSWORD_SUCCESS_MESSAGE, 'success');
    } catch (error) {
      logger.error?.('[forgot-password] Password reset failed', error);
      const message =
        String(error?.message || '').trim() === 'Valid email required'
          ? 'Enter a valid email address.'
          : FORGOT_PASSWORD_FAILURE_MESSAGE;
      setResponse(message, 'error');
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  }

  /**
   * Attach DOM listeners for the forgot-password page.
   *
   * @returns {void}
   */
  function init() {
    if (!form) return;
    form.addEventListener('submit', handleSubmit);
  }

  return {
    init,
    handleSubmit,
    requestPasswordResetEmail,
  };
}

const forgotPasswordController = createForgotPasswordPageController();
forgotPasswordController.init();
